import { computed } from 'vue';
import backendApi from '@/axios/backendApi';
import { fetchLightCultures } from './fetch_tables';
import { useDataStore } from '@/stores/useDataStore';
import { useToast } from 'vue-toastification'
import { createOrUpdateManualValue } from './functions_pilotage_api';

const toast = useToast()

export function isMainSpecie(id: number | null | undefined): boolean {
  return (
    id === 1 ||
    id === 2
  );
}

export function isSpecieMi456(id: number | null | undefined): boolean {
  return (
    id === 2
  );
}

export function isSpecieMi123(id: number | null | undefined): boolean {
  return (
    id === 1
  );
}

export const filteredCultures = computed(() => {
  const dataStore = useDataStore();
  let culturesFiltered: Culture[] = dataStore.showInactive
    ? dataStore.cultures
    : dataStore.cultures.filter((c: Culture) => c.active);

  if (dataStore.filtreEspece) {
    culturesFiltered = culturesFiltered.filter(
      (c: Culture) => c.specie?.id === dataStore.filtreEspece
    );
  }

  if (dataStore.filtrePBR) {
    culturesFiltered = culturesFiltered.filter(
      (c: Culture) => c.pbr?.id === dataStore.filtrePBR
    );
  }

  culturesFiltered = culturesFiltered.sort((a: Culture, b: Culture) => {
    const numA = parseInt(a.pbr.numero_pbr.replace(/[^\d]/g, '')) || 0;
    const numB = parseInt(b.pbr.numero_pbr.replace(/[^\d]/g, '')) || 0;

    if (numA !== numB) return numA - numB;

    const [yearA, dayA] = a.startdaycode.startday_code.split('-').map(Number);
    const [yearB, dayB] = b.startdaycode.startday_code.split('-').map(Number);

    return yearA !== yearB ? yearA - yearB : dayA - dayB;
  });

  return culturesFiltered;
});

export const filteredCulturesRecolte = computed(() => {
  const dataStore = useDataStore();

  // On ne garde que les cultures actives, sans autre filtre
  const culturesActives = dataStore.cultures.filter((c: Culture) => c.active);

  // On trie par numéro PBR puis par startday_code (année puis jour)
  culturesActives.sort((a: Culture, b: Culture) => {
    const numA = parseInt(a.pbr.numero_pbr.replace(/[^\d]/g, '')) || 0;
    const numB = parseInt(b.pbr.numero_pbr.replace(/[^\d]/g, '')) || 0;

    if (numA !== numB) return numA - numB;

    const [yearA, dayA] = a.startdaycode.startday_code.split('-').map(Number);
    const [yearB, dayB] = b.startdaycode.startday_code.split('-').map(Number);

    return yearA !== yearB ? yearA - yearB : dayA - dayB;
  });

  return culturesActives;
});

export async function deactivateCulture(t: (key: string) => string) {
  const dataStore = useDataStore();

  // Vérifier si la culture est sélectionnée et que l'ID est présent
  if (!dataStore.selectedCulture?.id) {
    console.error(t('cultureNotSelected'));
    return; // Retourne ici si la culture n'est pas valide
  }

  try {
    // Envoie de la requête PATCH pour désactiver la culture
    await backendApi.patch(`/cultures/${dataStore.selectedCulture.id}/deactivate`);

    // Supprimer les manualValues liés à cette culture en envoyant des valeurs vides
    await createOrUpdateManualValue({
      id: dataStore.manualValues[dataStore.selectedCulture.id]?.id ?? 0, // ou undefined si tu veux créer
      culture_id: dataStore.selectedCulture.id,
      physiologie: null,
      actions_prevue: null,
      prochaine_recolte: null,
      last_updated: null,
    });

    // Actualiser la liste des cultures
    await fetchLightCultures();

    // Réinitialiser la culture sélectionnée
    dataStore.selectedCulture = null;

    toast.success(t('cultureDeactivatedSuccess'));

  } catch (error) {
    console.error(t('cultureDeactivatedError'), error);
    toast.error(t('cultureDeactivatedError'));
  }
}

export async function reactivateCulture(t: (key: string) => string) {
  const dataStore = useDataStore();

  // Vérifier si la culture est sélectionnée et que l'ID est présent
  if (!dataStore.selectedCulture?.id) {
    console.error(t('cultureNotSelected'));
    return; // Retourne ici si la culture n'est pas valide
  }

  try {
    // Envoie de la requête PATCH pour réactiver la culture
    await backendApi.patch(`/cultures/${dataStore.selectedCulture.id}/reactivate`);

    // Actualiser la liste des cultures
    await fetchLightCultures();

    // Réinitialiser la culture sélectionnée
    dataStore.selectedCulture = null;

    toast.success(t('cultureReactivatedSuccess'));

  } catch (error) {
    console.error(t('cultureReactivatedError'), error);
    toast.error(t('cultureReactivatedError'));
  }
}

export async function createCulture(t: (key: string) => string) {
  const dataStore = useDataStore();

  try {
    // Créer ou récupérer le startdaycode
    const { data: createdSDC } = await backendApi.post('/startdaycodes', {
      startday_code: dataStore.newCulture.startdaycode.startday_code,
    });

    dataStore.newCulture.startdaycode.id = createdSDC.id;

    // Construire le payload en extrayant les IDs requis
    const payload = {
      id: dataStore.newCulture.id,
      active: dataStore.newCulture.active,
      specie_id: dataStore.newCulture.specie?.id,
      strain_id: dataStore.newCulture.strain?.id,
      pbr_id: dataStore.newCulture.pbr?.id,
      startdaycode_id: dataStore.newCulture.startdaycode?.id,
    };

    await backendApi.post('/cultures', payload);

    dataStore.showCreateCulture = false;
    await fetchLightCultures();

    toast.success(t('cultureCreatedSuccess'));
  } catch (error) {
    console.error(t('cultureCreatedError'), error);
    toast.error(t('cultureCreatedError'));
  }
}

export const groupedCultures = computed(() => {
  const groups = {
    zoneA: [] as Culture[],
    zoneB: [] as Culture[],
    zoneC: [] as Culture[],
  };

  for (const culture of filteredCultures.value) {
    groups[culture.pbr.zone as keyof typeof groups].push(culture);
  }

  return groups;
});